import { useRef, useState } from 'react'
import { policyService } from '../services/policy'
import { Upload, CheckCircle, XCircle } from 'lucide-react'

interface PolicyPdfUploadProps {
  leadId: number
  policyNumber: string
}

export default function PolicyPdfUpload({ leadId, policyNumber }: PolicyPdfUploadProps) {
  const fileRef   = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult]       = useState<{ valid: boolean; hash: string } | null>(null)
  const [error, setError]         = useState('')

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.endsWith('.pdf')) {
      setError('Only PDF files are accepted')
      return
    }

    setUploading(true)
    setResult(null)
    setError('')
    try {
      const res = await policyService.uploadPdf(leadId, file)
      setResult(res)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Upload failed')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <label
        className={`btn-secondary cursor-pointer flex items-center text-sm ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <Upload className="h-4 w-4 mr-2" />
        {uploading ? 'Uploading…' : 'Upload Policy PDF'}
        <input
          ref={fileRef}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={handleUpload}
          disabled={uploading}
        />
      </label>

      {result && (
        <span
          className="flex items-center gap-1 text-sm"
          style={{ color: result.valid ? 'var(--success-text)' : 'var(--danger-text)' }}
        >
          {result.valid
            ? <><CheckCircle className="h-4 w-4" /> PDF verified for {policyNumber}</>
            : <><XCircle className="h-4 w-4" /> PDF hash mismatch — check document</>
          }
        </span>
      )}

      {error && (
        <span className="text-sm" style={{ color: 'var(--danger-text)' }}>{error}</span>
      )}
    </div>
  )
}
